<template>
  <div :class="containerClass">
    <page-header
      :title="this.$route.params.id ? 'Edit Advisor' : 'Add New Advisor'"
      :container-class="null"
      class="mb-16pt"
    />

    <b-form class="col-md-12 px-0 page-section pt-0 adv-form" @submit.prevent="onSubmit">
      <!-- TransitionU creators: the profile is owned by Studio and pushed here on every save. -->
      <div v-if="isCreator" class="adv-creator">
        <div class="adv-creator__icon"><md-icon>verified_user</md-icon></div>
        <div class="adv-creator__body">
          <div class="adv-creator__title">
            TransitionU creator
            <b-badge :variant="isAdvisorActive ? 'success' : 'warning'" class="ml-2">{{ isAdvisorActive ? 'Live on CareCampus' : 'Awaiting approval' }}</b-badge>
          </div>
          <div class="adv-creator__meta">
            Profile managed in TransitionU Studio and synced here on every save.
            <span v-if="advisor.transitionu_synced_at">Last synced {{ formatDateTime(advisor.transitionu_synced_at) }}.</span>
          </div>
        </div>
        <div class="adv-creator__actions">
          <a v-if="advisor.transitionu_handle" :href="`https://transitionu.health/@${advisor.transitionu_handle}`" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary">
            View page
          </a>
          <a v-if="advisor.transitionu_creator_id" :href="`https://api.transitionu.health/admin/creators/creatorprofile/${advisor.transitionu_creator_id}/change/`" target="_blank" rel="noopener" class="btn btn-sm btn-primary ml-2">
            {{ isAdvisorActive ? 'Open in Studio' : 'Approve in Studio' }}
          </a>
        </div>
      </div>

      <!-- Basics -->
      <h5 class="adv-section">Basics</h5>

      <b-media v-if="isCreator" class="align-items-center mb-3" vertical-align="center">
        <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
          <b-img :src="advisor.image" class="img-fluid" width="40" alt="" v-if="advisor.image" />
          <md-icon v-else>account_circle</md-icon>
        </fmv-avatar>
        <div>
          <h6 class="mb-0">{{ advisor.first_name }} {{ advisor.last_name }}</h6>
          <small class="text-muted">{{ advisor.email }}<span v-if="advisor.phone"> · {{ advisor.phone }}</span></small>
        </div>
      </b-media>

      <template v-else>
        <b-form-group label="Photo" label-for="image" label-class="form-label" class="row-align-items-center">
          <b-media class="align-items-center" vertical-align="center">
            <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
              <span v-if="isImgUploading">...</span>
              <b-img :src="advisor.image" class="img-fluid" width="40" alt="Image" v-else-if="advisor.image" />
              <md-icon v-else>account_circle</md-icon>
            </fmv-avatar>
            <image-uploader
              @image="e => setImage(e)"
              :is-uploading="isImgUploading"
              placeholder="Select Image"
              :state="!$v.advisor.image.required && $v.advisor.image.$dirty"
              state-message="This field is required"
            />
          </b-media>
        </b-form-group>

        <div class="row">
          <b-form-group label="First Name" label-for="firstName" label-class="form-label" class="col-md-6">
            <b-form-input id="firstName" placeholder="First name" v-model="advisor.first_name"
              :state="!$v.advisor.first_name.required && $v.advisor.first_name.$dirty ? false : null" />
            <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label="Last Name" label-for="lastName" label-class="form-label" class="col-md-6">
            <b-form-input id="lastName" placeholder="Last name" v-model="advisor.last_name"
              :state="!$v.advisor.last_name.required && $v.advisor.last_name.$dirty ? false : null" />
            <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
          </b-form-group>
        </div>

        <div class="row">
          <b-form-group label="Email" label-for="email" label-class="form-label" class="col-md-6">
            <b-form-input id="email" placeholder="name@example.com" v-model="advisor.email"
              :state="(!$v.advisor.email.required || !$v.advisor.email.email) && $v.advisor.$dirty ? false : null" />
            <b-form-invalid-feedback v-if="!$v.advisor.email.email">Invalid email. Please use the format name@example.com.</b-form-invalid-feedback>
            <b-form-invalid-feedback v-else>This field is required.</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label="Phone" label-for="phone" label-class="form-label" class="col-md-6">
            <b-form-input id="phone" placeholder="(888) 689 - 1235" v-model="advisor.phone" v-mask="'(###) ### - ####'"
              :state="!$v.advisor.phone.isCorrectFormat && $v.advisor.phone.$dirty ? false : null" />
            <b-form-invalid-feedback>Invalid phone number. Please use the format (XXX) XXX - XXXX.</b-form-invalid-feedback>
          </b-form-group>
        </div>
      </template>

      <b-form-group label-class="form-label" class="mb-0">
        <b-form-checkbox v-model="isAdvisorActive" name="check-button" switch>
          {{
            get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN
              ? advisor.advisor_association === 'university'
                ? 'Show advisor on CareCampus'
                : 'Show advisor on organization website'
              : 'Show advisor on your website'
          }}
        </b-form-checkbox>
      </b-form-group>

      <!-- Creators: what Studio sent, read only -->
      <template v-if="isCreator">
        <h5 class="adv-section">From Studio</h5>
        <div class="row">
          <div class="col-md-6 mb-3" v-for="row in studioSummary" :key="row.label">
            <small class="text-muted d-block">{{ row.label }}</small>
            <span :class="row.value ? '' : 'text-muted font-italic'">{{ row.value || 'Not added yet' }}</span>
          </div>
        </div>
      </template>

      <!-- Profile -->
      <template v-if="!isCreator">
        <h5 class="adv-section">Profile</h5>

        <b-form-group label="About" label-for="description" label-class="form-label">
          <editor
            :init="{
              height: 320,
              menubar: false,
              plugins: ['advlist autolink lists link paste wordcount'],
              toolbar: 'undo redo | bold italic | bullist numlist | link | removeformat',
            }"
            v-model="advisor.description"
            placeholder="A few sentences about the advisor."
          />
          <b-form-invalid-feedback :state="!$v.advisor.description.required && $v.advisor.$dirty ? false : null">
            This field is required.
          </b-form-invalid-feedback>
        </b-form-group>

        <div class="row">
          <b-form-group label="Button text" label-for="cta_text" label-class="form-label" class="col-md-6">
            <b-form-input id="cta_text" placeholder="Schedule a consultation" v-model.trim="advisor.cta_text" />
          </b-form-group>
          <b-form-group label="Button link" label-for="cta_link" label-class="form-label" class="col-md-6">
            <b-form-input id="cta_link" placeholder="https://calendly.com/..." v-model.trim="advisor.cta_link"
              :state="!$v.advisor.cta_link.url && $v.advisor.cta_link.$dirty ? false : null" />
            <b-form-invalid-feedback>Invalid URL. Please use the format https://example.com.</b-form-invalid-feedback>
          </b-form-group>
        </div>

        <div class="row">
          <b-form-group label="Program" label-for="offered_programs" label-class="form-label" class="col-md-6">
            <v-select id="offered_programs" class="form-control v-select-custom" label="title" v-model="advisor.program"
              :reduce="opt => opt.id" placeholder="Select program" :options="allPrograms" :loading="areProgramsLoading" />
          </b-form-group>
          <b-form-group label="Background" label-for="background" label-class="form-label" class="col-md-6">
            <v-select id="background" class="form-control v-select-custom" label="text" v-model="advisor.background"
              :reduce="opt => opt.value" placeholder="Select background" :options="ADVISOR_BACKGROUND" />
          </b-form-group>
        </div>

        <!-- Universities (superadmin, university advisors) -->
        <template v-if="get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN && advisor.advisor_association === 'university'">
          <h5 class="adv-section">Universities</h5>
          <b-form-group label="Affiliated universities" label-for="universities" label-class="form-label">
            <v-select id="universities" class="form-control v-select-custom" label="name" v-model="advisor.universities"
              :reduce="u => u.id" multiple placeholder="Search universities" :options="universitiesOptions" />
            <small class="text-muted">The advisor shows on each selected university's CareCampus page.</small>
          </b-form-group>
        </template>

        <!-- Experience -->
        <div class="adv-section d-flex align-items-center justify-content-between">
          <h5 class="mb-0">Experience</h5>
          <b-btn variant="outline-primary" size="sm" :disabled="isLoading" @click.prevent="openWorkExpModal()">
            <i class="fas fa-plus mr-1"></i>Add
          </b-btn>
        </div>
        <div v-for="(exp, index) in advisor.experience" :key="`adv-exp-${index}`" class="adv-row">
          <div class="adv-row__body">
            <div class="adv-row__title">{{ exp.title }}<span v-if="exp.employer" class="text-muted font-weight-normal"> · {{ exp.employer }}</span></div>
            <div class="text-muted small">
              {{ formatExpDate(exp.start_date) }} to {{ exp.still_working ? 'Present' : formatExpDate(exp.end_date) }}
              <span v-if="exp.city || exp.state"> · {{ [exp.city, exp.state].filter(Boolean).join(', ') }}</span>
            </div>
            <ul v-if="get(exp, 'work_tasks.length')" class="adv-row__tasks">
              <li v-for="(task, tIndex) in exp.work_tasks" :key="`exp-task-${tIndex}`">{{ task }}</li>
            </ul>
          </div>
          <div class="adv-row__actions">
            <b-btn variant="light" size="sm" :disabled="isLoading" title="Edit" @click.prevent="openWorkExpModal(exp, index)"><i class="fas fa-pen"></i></b-btn>
            <b-btn variant="light" size="sm" :disabled="isLoading" title="Remove" class="ml-1" @click.prevent="removeExpEduItem(index, 'experience', 'experience')"><i class="fas fa-trash text-danger"></i></b-btn>
          </div>
        </div>
        <p v-if="!get(advisor, 'experience.length')" class="adv-empty">No experience added.</p>

        <!-- Education -->
        <div class="adv-section d-flex align-items-center justify-content-between">
          <h5 class="mb-0">Education</h5>
          <b-btn variant="outline-primary" size="sm" :disabled="isLoading" @click.prevent="openEducationModal()">
            <i class="fas fa-plus mr-1"></i>Add
          </b-btn>
        </div>
        <div v-for="(education, index) in advisor.education" :key="`adv-edu-${index}`" class="adv-row">
          <div class="adv-row__body">
            <div class="adv-row__title">{{ $t(`resumeEducationTypes.${education.type}`) }}, {{ education.study }}<span v-if="education.institution" class="text-muted font-weight-normal"> · {{ education.institution }}</span></div>
            <div class="text-muted small">
              {{ formatExpDate(education.start_date) }} to {{ education.still_attending ? 'Present' : formatExpDate(education.end_date) }}
              <span v-if="education.city || education.state"> · {{ [education.city, education.state].filter(Boolean).join(', ') }}</span>
            </div>
          </div>
          <div class="adv-row__actions">
            <b-btn variant="light" size="sm" :disabled="isLoading" title="Edit" @click.prevent="openEducationModal(education, index)"><i class="fas fa-pen"></i></b-btn>
            <b-btn variant="light" size="sm" :disabled="isLoading" title="Remove" class="ml-1" @click.prevent="removeExpEduItem(index, 'education', 'education')"><i class="fas fa-trash text-danger"></i></b-btn>
          </div>
        </div>
        <p v-if="!get(advisor, 'education.length')" class="adv-empty">No education added.</p>

        <!-- Specialities -->
        <div class="adv-section d-flex align-items-center justify-content-between">
          <h5 class="mb-0">Specialities</h5>
          <b-btn variant="outline-primary" size="sm" :disabled="isLoading" @click.prevent="addSpeciality()">
            <i class="fas fa-plus mr-1"></i>Add
          </b-btn>
        </div>
        <div v-for="(v, index) in $v.advisor.expertise.$each.$iter" :key="`adv-spec-${get(v, 'id')}`" class="d-flex align-items-start mb-2">
          <div class="flex-grow-1 mr-2">
            <b-form-input :id="`speciality-${v.id}`" placeholder="e.g. NCLEX prep" v-model.trim="v.name.$model"
              :state="!v.name.required && v.name.$dirty ? false : null" maxlength="50" />
            <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
          </div>
          <b-btn variant="light" size="sm" :disabled="isLoading" title="Remove" @click.prevent="removeSpeciality(index)"><i class="fas fa-trash text-danger"></i></b-btn>
        </div>
        <p v-if="!get(advisor, 'expertise.length')" class="adv-empty">No specialities added.</p>
      </template>

      <!-- Affiliations: logos, the one thing not managed in Studio -->
      <div class="adv-section d-flex align-items-center justify-content-between">
        <div>
          <h5 class="mb-0">Affiliations</h5>
          <small class="text-muted">Logos of organizations the advisor is affiliated with.</small>
        </div>
        <b-btn variant="outline-primary" size="sm" :disabled="isLoading" @click.prevent="addAffiliation()">
          <i class="fas fa-plus mr-1"></i>Add
        </b-btn>
      </div>
      <div v-for="(affiliation, index) in affiliations" :key="`adv-aff-${affiliation.id}`" class="d-flex align-items-center mb-2">
        <div class="flex-grow-1 mr-2">
          <b-media class="align-items-center" vertical-align="center">
            <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
              <span v-if="isImgUploading">...</span>
              <b-img :src="affiliation.image" class="img-fluid" width="40" alt="Image" v-else-if="affiliation.image" />
              <i class="fas fa-university fa-lg" v-else></i>
            </fmv-avatar>
            <image-uploader @image="e => uploadAffliationImage(e, index)" :is-uploading="affiliation.isUploading" placeholder="Select Image" />
          </b-media>
        </div>
        <b-btn variant="light" size="sm" :disabled="isLoading" title="Remove" @click.prevent="removeAffiliation(index)"><i class="fas fa-trash text-danger"></i></b-btn>
      </div>
      <p v-if="!get(affiliations, 'length')" class="adv-empty">No affiliations added.</p>

      <!-- What the creator still has to fill in at Studio -->
      <template v-if="isCreator && missingStudioFields.length">
        <h5 class="adv-section">Missing in Studio</h5>
        <p class="text-muted small mb-2">The creator fills these in at studio.transitionu.health. They sync here on their next save.</p>
        <b-badge v-for="f in missingStudioFields" :key="f" variant="none" class="border border-warning text-dark mr-1 mb-1">{{ f }}</b-badge>
      </template>

      <div class="mt-4">
      <b-btn variant="primary" :disabled="isFormLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? 'Update' : 'Save' }}</span>
      </b-btn>
      </div>
    </b-form>

    <adv-exp-modal
      :show-modal="modals.showWorkExpModal"
      :work-exp="modals.selectedWorkExp"
      :exp-index="modals.selectedWorkExpIndex"
      @close="hideWorkExpModal"
      @closeUpdate="hideUpdateWorkExpModal"
    >
    </adv-exp-modal>

    <adv-education-modal
      :show-modal="modals.showEducationModal"
      :education="modals.selectedEducation"
      :item-index="modals.selectedEduIndex"
      @close="hideEducationModal"
      @closeUpdate="hideUpdateEducationModal"
    >
    </adv-education-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import PageHeader from '@/components/Ui/PageHeader.vue';
import Editor from '@tinymce/tinymce-vue';
import { FmvAvatar } from 'fmv-avatar';
import { get, orderBy } from 'lodash';
import Page from '@/components/Page.vue';
import { USER_ROLES } from '@/common/constants';
import { parseFileNameFromUrl, formatDateTime } from '@/common/utils';
import { required, email, url } from 'vuelidate/lib/validators';
import 'vue-swatches/dist/vue-swatches.css';
import ImageUploader from '@/components/ImageUploader.vue';
import AdvExpModal from '@/components/Advisors/AdvExpModal.vue';
import AdvEducationModal from '@/components/Advisors/AdvEducationModal.vue';
import { PHONE_FORMAT_REGEX } from '@/common/constants';
import moment from 'moment';
import { uuid } from 'vue-uuid';
import { ADVISOR_BACKGROUND } from '@/common/constants';

export default {
  components: {
    PageHeader,
    Editor,
    FmvAvatar,
    ImageUploader,
    AdvExpModal,
    AdvEducationModal,
  },
  extends: Page,

  data() {
    return {
      title: this.$route.params.id ? 'Edit Advisor' : 'Add Advisor',
      advisor: {
        image: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        description: '',
        education: [],
        expertise: [],
        experience: [],
        affiliations: [],
        cta_link: '',
        cta_text: '',
        advisor_association: '',
        universities: [],
        program: null,
        background: null,
      },
      isAdvisorActive: null,
      affiliations: [],
      universitiesOptions: [],

      allPrograms: [],

      isLoading: false,
      isImgUploading: false,
      areProgramsLoading: false,

      USER_ROLES,
      ADVISOR_BACKGROUND,

      modals: {
        showWorkExpModal: false,
        selectedWorkExp: null,
        selectedWorkExpIndex: null,

        showEducationModal: false,
        selectedEducation: null,
        selectedEduIndex: null,
      },
    };
  },
  validations() {
    return {
      advisor: {
        image: {},
        first_name: { required },
        last_name: { required },
        email: { required, email },
        phone: {
          isCorrectFormat(value) {
            return PHONE_FORMAT_REGEX.test(value);
          },
        },
        description: { required },
        expertise: {
          $each: { name: { required } },
        },
        cta_link: { url },
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    isFormLoading() {
      return this.isLoading || this.isImgUploading || this.affiliations.some(a => a.isUploading);
    },
    isCreator() {
      return !!this.advisor.is_transitionu_creator;
    },
    studioSummary() {
      const a = this.advisor;
      const uni = (a.universities || []).map(id => get(this.universitiesOptions.find(u => u.id === id), 'name')).filter(Boolean);
      const program = get(this.allPrograms.find(pr => pr.id === a.program), 'title');
      const background = get(ADVISOR_BACKGROUND.find(b => b.value === a.background), 'text');
      const about = (a.description || '').replace(/<[^>]+>/g, '').trim();
      return [
        { label: 'School', value: uni.join(', ') },
        { label: 'Program', value: program },
        { label: 'About', value: about.length > 160 ? `${about.slice(0, 160)}…` : about },
        { label: 'Specialities', value: (a.expertise || []).map(e => e.name || e).join(', ') },
        { label: 'Education', value: (a.education || []).length ? `${a.education.length} added` : '' },
        { label: 'Experience', value: (a.experience || []).length ? `${a.experience.length} added` : '' },
        { label: 'Background', value: background },
        { label: 'Booking link', value: a.cta_link },
      ];
    },
    missingStudioFields() {
      const a = this.advisor;
      const checks = [
        ['Photo', a.image], ['About', (a.description || '').replace(/<[^>]+>/g, '').trim()], ['Phone', a.phone],
        ['School', (a.universities || []).length], ['Program', a.program], ['Specialities', (a.expertise || []).length],
        ['Education', (a.education || []).length], ['Experience', (a.experience || []).length], ['Background', a.background],
        ['Booking link', a.cta_link],
      ];
      return checks.filter(([, v]) => !v).map(([label]) => label);
    },
  },

  methods: {
    formatDateTime,
    ...mapActions('advisors', ['getAdvisor', 'createAdvisor', 'updateAdvisor']),
    ...mapActions('employer', ['getEmployerUploadPresignedUrl']),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    ...mapActions('fileDownload', ['downloadFile']),
    ...mapActions('uni', ['getAllUniversitiesByState']),
    ...mapActions('program', ['getAllPrograms']),

    get,
    parseFileNameFromUrl,

    async setImage(file) {
      this.isImgUploading = true;
      this.advisor.image = await this.uploadImage(file);
      this.isImgUploading = false;
    },

    async uploadAffliationImage(file, index) {
      this.affiliations[index].isUploading = true;
      this.affiliations[index].image = await this.uploadImage(file);
      this.affiliations[index].isUploading = false;
    },

    async fetchPrograms() {
      this.areProgramsLoading = true;
      const response = await this.getAllPrograms();
      this.allPrograms = response.data;
      this.areProgramsLoading = false;
    },

    async uploadImage(file) {
      try {
        const urlResp = await this.getEmployerUploadPresignedUrl({
          file_name: file.name,
          content_type: file.type,
          upload_type: 'advisors',
        });
        await this.uploadToPresignedUrl({ url: urlResp.upload_url, file });
        return urlResp.upload_url.split('?')[0];
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: "Couldn't upload image. Please try again." });
      }
      return null;
    },

    async fetchUniversities() {
      const response = await this.getAllUniversitiesByState({ basic: true });
      this.universitiesOptions = response.data;
    },

    selectUniversity(id) {
      if (this.advisor.universities.includes(id)) {
        const index = this.advisor.universities.indexOf(id);
        if (index > -1) {
          this.advisor.universities.splice(index, 1);
        }
      } else {
        this.advisor.universities.push(id);
      }
    },

    async onSubmit() {
      this.isLoading = true;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          const affiliations = this.affiliations.filter(a => a.image).map(a => a.image);
          // Creators are managed in Studio: only the switch and affiliations are ours to write.
          const data = this.advisor.is_transitionu_creator ? {
            affiliations,
            status: this.isAdvisorActive ? 'active' : 'inactive',
          } : {
            ...this.advisor,
            affiliations,
            status: this.isAdvisorActive ? 'active' : 'inactive',

            ...(get(this.getLoggedInUser, 'role') === USER_ROLES.ORGANIZATION && {
              organization: this.getLoggedInUser.linked_entity.id,
              advisor_association: 'organization',
            }),
          };
          if (this.$route.params.id) {
            await this.updateAdvisor({
              id: this.$route.params.id,
              data,
            });
            this.makeToast({ variant: 'success', msg: 'Advisor updated!' });
          } else {
            await this.createAdvisor({
              ...data,
            });
            this.makeToast({ variant: 'success', msg: 'Advisor added!' });
          }
          setTimeout(() => {
            if (get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN) {
              this.$router.push({ name: 'sa-manage-advisors' });
            } else {
              this.$router.push({ name: 'manage-org-advisors' });
            }
          }, 250);
        } catch (err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields correctly.' });
      }
      this.isLoading = false;
    },

    openWorkExpModal(workExp = null, index = null) {
      this.modals.selectedWorkExp = workExp;
      this.modals.selectedWorkExpIndex = index;
      this.modals.showWorkExpModal = true;
    },
    hideWorkExpModal() {
      this.modals.showWorkExpModal = false;
      this.modals.selectedWorkExp = null;
      this.modals.selectedWorkExpIndex = null;
    },
    hideUpdateWorkExpModal(data) {
      this.modals.showWorkExpModal = false;
      this.modals.selectedWorkExp = null;
      this.modals.selectedWorkExpIndex = null;

      if (data.isUpdate) {
        this.advisor.experience[data.index] = data.workExp;
      } else {
        this.advisor.experience.push(data.workExp);
      }
      this.advisor.experience = orderBy(this.advisor.experience, ['still_working', 'start_date'], ['desc', 'desc']);
    },

    openEducationModal(education = null, index = null) {
      this.modals.selectedEducation = education;
      this.modals.selectedEduIndex = index;
      this.modals.showEducationModal = true;
    },
    hideEducationModal() {
      this.modals.showEducationModal = false;
      this.modals.selectedEducation = null;
      this.modals.selectedEduIndex = null;
    },
    hideUpdateEducationModal(data) {
      this.modals.showEducationModal = false;
      this.modals.selectedEducation = null;
      this.modals.selectedEduIndex = null;

      if (data.isUpdate) {
        this.advisor.education[data.index] = data.education;
      } else {
        this.advisor.education.push(data.education);
      }
      this.advisor.education = orderBy(this.advisor.education, ['still_attending', 'start_date'], ['desc', 'desc']);
    },

    async removeExpEduItem(index, type, itemKey) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Please confirm that you want to delete the selected ${type}.`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          },
        );
        if (isConfirmed) {
          this.advisor[itemKey].splice(index, 1);
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    addSpeciality() {
      this.advisor.expertise.push({ name: '', id: uuid.v4() });
    },
    removeSpeciality(index) {
      this.advisor.expertise.splice(index, 1);
    },

    addAffiliation() {
      this.affiliations.push({ image: '', isUploading: false, id: uuid.v4() });
    },
    removeAffiliation(index) {
      this.affiliations.splice(index, 1);
    },

    formatExpDate(date) {
      return moment(date).format('MMMM, YYYY');
    },
  },

  async mounted() {
    this.isLoading = true;
    this.fetchPrograms();

    if (get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN) {
      this.advisor.advisor_association = 'university';
      this.fetchUniversities();
    } else {
      this.advisor.advisor_association = 'organization';
    }

    try {
      if (this.$route.params.id) {
        const resp = (await this.getAdvisor(this.$route.params.id)).data;
        this.advisor = { ...resp, program: get(resp, 'program.id') };
        this.affiliations = this.advisor.affiliations.map(a => ({ image: a, isUploading: false, id: uuid.v4() }));
        this.isAdvisorActive = this.advisor.status === 'active';
      }
    } catch (e) {
      this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      this.$router.push({
        name: 'manage-org-advisors',
      });
    }

    this.isLoading = false;
  },
};
</script>

<style lang="scss" scoped>
.adv-form .adv-section {
  margin: 28px 0 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
  font-size: 15px;
  font-weight: 700;
}
.adv-form .adv-section:first-of-type { margin-top: 4px; }
.adv-form .adv-section h5 { font-size: 15px; font-weight: 700; }
.adv-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f5;
}
.adv-row__body { min-width: 0; flex: 1; }
.adv-row__title { font-weight: 600; }
.adv-row__tasks { margin: 4px 0 0; padding-left: 18px; font-size: 13px; color: #6c757d; }
.adv-row__actions { flex: none; white-space: nowrap; }
.adv-empty { color: #6c757d; font-style: italic; font-size: 13px; margin: 0; }
.adv-creator {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  margin-bottom: 20px;
  border: 1px solid #cfe0fb;
  border-radius: 10px;
  background: #eef4fe;
}
.adv-creator__icon {
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: #fff;
  color: #245fcc;
}
.adv-creator__body { flex: 1; min-width: 0; }
.adv-creator__title { font-weight: 700; color: #181818; display: flex; align-items: center; flex-wrap: wrap; }
.adv-creator__meta { font-size: 12.5px; color: #4b5563; margin-top: 2px; }
.adv-creator__actions { flex: none; display: flex; align-items: center; }
@media (max-width: 767px) {
  .adv-creator { flex-wrap: wrap; }
  .adv-creator__actions { width: 100%; margin-top: 8px; }
}
</style>
